/**
 * Component: sellerie-app/frontend/src/pages/Auth/ResetPassword.tsx
 * Form to submit a new password using a reset token.
 */

import React, { useContext, useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AuthContext from '../../contexts/AuthContext';
import { apiFetch } from '../../services/httpClient';

const ResetPassword: React.FC = () => {
  const { resetPassword, loading, error } = useContext(AuthContext);
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendMessage, setResendMessage] = useState('');
  const [resendError, setResendError] = useState('');
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    if (!token) {
      setFormError('Lien invalide ou manquant.');
    }
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    setSuccessMessage('');
    setResendMessage('');
    setResendError('');

    if (!token) {
      setFormError('Lien invalide ou manquant.');
      return;
    }

    if (!password || !confirmPassword) {
      setFormError('Veuillez remplir tous les champs.');
      return;
    }

    if (password !== confirmPassword) {
      setFormError('Les mots de passe ne correspondent pas.');
      return;
    }

    try {
      await resetPassword(token, password);
      setSuccessMessage('Mot de passe reinitialise. Vous pouvez vous connecter.');
      setTimeout(() => navigate('/login'), 1500);
    } catch (err) {
      console.error('Erreur reset mot de passe:', err);
    }
  };

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    setResendMessage('');
    setResendError('');

    const email = resendEmail.trim().toLowerCase();
    if (!email) {
      setResendError('Email requis.');
      return;
    }

    try {
      setIsResending(true);
      const resp = await apiFetch<{ message?: string }>('/auth/password/resend-setup', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      setResendMessage(resp?.message || "Si un compte activable existe pour cette adresse, un nouveau lien a ete envoye.");
    } catch (err: any) {
      const msg = err?.message || 'Impossible denvoyer un nouveau lien.';
      setResendError(msg);
    } finally {
      setIsResending(false);
    }
  };

  const aggregatedError = formError || error || '';
  const isTokenInvalid = aggregatedError.toLowerCase().includes('token invalide') || aggregatedError.toLowerCase().includes('expire');

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Reinitialiser le mot de passe
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Choisissez un nouveau mot de passe.
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="new-password" className="sr-only">
                Nouveau mot de passe
              </label>
              <input
                id="new-password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                className="input-field rounded-t-md"
                placeholder="Nouveau mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="confirm-password" className="sr-only">
                Confirmation
              </label>
              <input
                id="confirm-password"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                className="input-field rounded-b-md"
                placeholder="Confirmez le mot de passe"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
          </div>

          {(formError || error) && (
            <div className="text-red-500 text-sm text-center">
              {formError || error}
            </div>
          )}

          {successMessage && (
            <div className="text-green-600 text-sm text-center">
              {successMessage}
            </div>
          )}

          <div className="space-y-3">
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {loading ? 'Mise a jour...' : 'Mettre a jour le mot de passe'}
            </button>
            <div className="text-sm text-center">
              <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500">
                Retour a la connexion
              </Link>
            </div>
          </div>
        </form>

        {isTokenInvalid && (
          <div className="mt-4 space-y-3 border rounded-md p-4 bg-white shadow-sm">
            <p className="text-sm text-gray-700">
              Ce lien n&apos;est plus valide. Vous pouvez demander un nouveau lien de configuration de mot de passe.
            </p>
            <form className="space-y-3" onSubmit={handleResend}>
              <input
                type="email"
                className="input-field"
                placeholder="Votre email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                required
              />
              {resendError && <div className="text-sm text-red-500">{resendError}</div>}
              {resendMessage && <div className="text-sm text-green-600">{resendMessage}</div>}
              <button
                type="submit"
                disabled={isResending}
                className="w-full py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {isResending ? 'Envoi...' : 'Recevoir un nouveau lien'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
